import { animateScroll } from 'components/ScrollTo'
import { updateAppUrl } from 'lib/url/route'
import { objectToQueryString, getDataLayerItemVariant } from 'lib/helpers'
import { parse } from 'lib/url/parse'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import getGoogleRecommendedArtworks from 'apis/google'
import { algoliaEventsApi } from 'apis/algolia'
import { fetchArtistArtworks, fetchRelatedArtworks } from '../../apis/carousel'
import { DEFAULT_APP } from '../../helpers/constants'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
// Recommendation API expects the following ATTRIBUTES
const PARAMS = {
  event: 'detail-page-view',
  pageSize: 25,
  placement: 'pdp-bottom',
  experiment: '',
}
const PLP_PARAMS = {
  event: 'category-page-view',
  pageSize: 28,
  placement: 'recently-viewed',
  experiment: '',
}

export const getArtistArtworks = async ({
  abTest,
  callBack,
  firstName,
  lastName,
  userId,
  products,
  sku,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const artworks = await fetchArtistArtworks({
    abTest,
    firstName,
    lastName,
    userId,
    products,
    sku,
  })
  callBack(artworks)
}
export const getRelatedArtworks = async ({
  abTest,
  artworkId,
  callBack,
  category,
  destinationZone,
  isArtist,
  mediums,
  metadata,
  sku,
  styles,
  subject,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const artworks = await fetchRelatedArtworks({
    abTest,
    artworkId,
    category,
    destinationZone,
    isArtist,
    mediums,
    metadata,
    sku,
    styles,
    subject,
  })
  callBack(artworks)
}
export const getGoogleArtworks = async ({
  artworkId,
  category,
  callBack,
  visitorId,
  filter,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const { payload: artworks, success } = await getGoogleRecommendedArtworks({
    ...PARAMS,
    artworkId,
    category,
    visitorId,
    filter,
  })

  if (success) {
    callBack(artworks)
  }
}
export const getGoogleArtworksPLP = async ({
  category,
  callBack,
  visitorId,
  pageCategories,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const { payload: artworks, success } = await getGoogleRecommendedArtworks({
    ...PLP_PARAMS,
    category,
    visitorId,
    pageCategories,
  })

  if (success) {
    callBack(artworks)
  }
}
export const goToArtwork = (href: string) => {
  const { path } = parse(href)
  // Parse the url and break into segments => art/Photography-White-Shirt/21482/1318155/view
  const [, type, title, artistId, artworkId, view] = path.split('/') // ignore first `/`

  const app = `${DEFAULT_APP}?${objectToQueryString({
    type,
    title,
    artistId,
    artworkId,
    view,
  })}`
  updateAppUrl({
    app,
    href,
  })
  animateScroll(
    {
      offset: 0,
      duration: 200,
    },
    null
  )
}
export const clickArtwork = (
  ev: React.SyntheticEvent<Record<string, any>>,
  algoliaSearchIndex: string,
  algoliaUserToken: string,
  userID: number
) => {
  ev.preventDefault()
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()
  const {
    id,
    position,
    list,
    name,
    category,
    listid,
    listname,
    artid,
    brand,
    price,
  } = ev.currentTarget.dataset
  window.dataLayer.push({
    event: 'productClick',
    ecommerce: {
      click: {
        actionField: {
          list, // What carousel
        },
        products: [
          {
            id,
            // Product sku
            position,
            // Location in the carousel
            name, // Artwork title
          },
        ],
      },
    },
  })
  // Clear the previous ecommerce object.
  window.dataLayer.push({
    ecommerce: null,
  })
  window.dataLayer.push({
    event: 'select_item',
    ecommerce: {
      item_list_id: listid,
      item_list_name: listname,
      items: [
        {
          item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
          item_id: id || '',
          item_name: name || '',
          item_brand: brand || '',
          item_art_id: artid || '',
          item_variant: getDataLayerItemVariant(id),
          index: position,
          item_list_id: listid,
          item_list_name: listname,
          affiliation: 'Online Store',
          price: convertFromCentsToDollars(price) || 0,
          quantity: 1,
        },
      ],
    },
  })
  goToArtwork(ev.currentTarget.href)
  // Algolia event
  algoliaEventsApi({
    events: [
      {
        eventName: 'select_item',
        eventType: 'click',
        index: algoliaSearchIndex,
        userToken: algoliaUserToken,
        authenticatedUserToken: String(userID) || '',
        objectIDs: [String(artid)],
        positions: [Number(position)],
        ...(algoliaQueryIdFromLocalStorage && {
          queryID: algoliaQueryIdFromLocalStorage,
        }), // pass queryID only if its present, otherwise do not pass
      },
    ],
  })
}
export const clickArtworkPLPCarousel = (
  ev: React.SyntheticEvent<Record<string, any>>,
  algoliaSearchIndex: string,
  algoliaUserToken: string,
  userID: number
) => {
  ev.preventDefault()
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()
  const {
    id,
    position,
    list,
    name,
    category,
    listid,
    listname,
    artid,
    brand,
    price,
  } = ev.currentTarget.dataset
  // Delete this dataLayer event in July as part of universal analytics sunset
  window.dataLayer.push({
    event: 'productClick',
    ecommerce: {
      click: {
        actionField: {
          list, // What carousel
        },
        products: [
          {
            id,
            // Product sku
            position,
            // Location in the carousel
            name, // Artwork title
          },
        ],
      },
    },
  })
  // This is the new dataLayer event for GA4
  // Clear the previous ecommerce object.
  window.dataLayer.push({
    ecommerce: null,
  })
  window.dataLayer.push({
    event: 'select_item',
    ecommerce: {
      item_list_id: listid,
      item_list_name: listname,
      items: [
        {
          item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
          item_id: id || '',
          item_name: name || '',
          item_brand: brand || '',
          item_art_id: artid || '',
          item_variant: getDataLayerItemVariant(id),
          index: position,
          item_list_id: listid,
          item_list_name: listname,
          affiliation: 'Online Store',
          price: convertFromCentsToDollars(price) || 0,
          quantity: 1,
        },
      ],
    },
  })
  window.location.href = ev.currentTarget.href
  // Algolia event
  algoliaEventsApi({
    events: [
      {
        eventName: 'select_item',
        eventType: 'click',
        index: algoliaSearchIndex,
        userToken: algoliaUserToken,
        authenticatedUserToken: String(userID) || '',
        objectIDs: [String(artid)],
        positions: [position],
        ...(algoliaQueryIdFromLocalStorage && {
          queryID: algoliaQueryIdFromLocalStorage,
        }), // pass queryID only if its present, otherwise do not pass
      },
    ],
  })
}
